import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  InterestCopyRow,
  InterestCopyScope,
  useDeleteInterestCopyMutation,
  useGetInterestCatalogQuery,
  useGetInterestCopyQuery,
  useUpsertInterestCopyMutation,
} from '../redux/adminApiSlice';
import { gridTheme, gridTokens } from '../theme/gridTheme';

function linesToArray(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}
function arrayToLines(arr?: string[]): string {
  return (arr || []).join('\n');
}

/** What the editor dialog is targeting. */
interface EditTarget {
  scope: InterestCopyScope;
  key: string; // interest value / category key / '*'
  displayLabel: string;
  existing?: InterestCopyRow;
}

interface Draft {
  openers1v1: string; // one per line
  openersRoom: string; // one per line
  reviewed: boolean;
}

const multilineProps = {
  multiline: true,
  minRows: 4,
  maxRows: 14,
  fullWidth: true,
  size: 'small' as const,
};

/**
 * Explains what each `{...}` token actually renders as in the app. `{name}` /
 * `{value}` are the 1-on-1 tokens; `{People}` / `{n}` are the room-count tokens.
 * Shown at the top of the page and inside the editor so authors know what they
 * get.
 */
const PLACEHOLDERS: Array<{ token: string; becomes: string; example: string; where: string }> = [
  {
    token: '{name}',
    becomes: 'the OTHER person’s first name',
    example: '“Ask {name} their go-to karaoke song.” → “Ask Jack their go-to karaoke song.”',
    where: '1-on-1',
  },
  {
    token: '{value}',
    becomes: 'the shared interest itself',
    example: '“You’re both into {value}.” → “You’re both into Hiking.”',
    where: 'both',
  },
  {
    token: '{People}',
    becomes: 'the room count as words, capitalized (“Six people”, “One person”)',
    example: '“{People} here with a go-to karaoke song.” → “Six people here with a go-to karaoke song.”',
    where: 'room',
  },
  {
    token: '{n}',
    becomes: 'the room count as a bare number (“6”, “1”)',
    example: '“A lobby of {n}, basically.” → “A lobby of 6, basically.”',
    where: 'room',
  },
];

const PlaceholderLegend: React.FC = () => (
  <Box sx={{ display: 'grid', gap: 0.75 }}>
    {PLACEHOLDERS.map((p) => (
      <Box
        key={p.token}
        sx={{
          display: 'grid',
          gridTemplateColumns: '92px 1fr',
          alignItems: 'baseline',
          columnGap: 1.5,
          rowGap: 0.25,
        }}
      >
        <Box>
          <code>{p.token}</code>
        </Box>
        <Box>
          <Typography variant="body2" component="span">
            {p.becomes}
          </Typography>{' '}
          <Chip
            size="small"
            label={p.where === 'both' ? 'both' : p.where === 'room' ? 'room only' : '1-on-1 only'}
            variant="outlined"
            sx={{ height: 18, fontSize: 10, ml: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {p.example}
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const InterestCopyPage: React.FC = () => {
  const isMobile = useMediaQuery(gridTheme.breakpoints.down('sm'));
  const catalogQuery = useGetInterestCatalogQuery();
  const copyQuery = useGetInterestCopyQuery();
  const [upsert, { isLoading: saving }] = useUpsertInterestCopyMutation();
  const [remove] = useDeleteInterestCopyMutation();

  const [target, setTarget] = useState<EditTarget | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Index existing copy by "scope:key" for O(1) lookup while rendering the catalog.
  const byScopeKey = useMemo(() => {
    const m = new Map<string, InterestCopyRow>();
    for (const r of copyQuery.data?.items || []) {
      m.set(`${r.scope}:${r.key}`, r);
    }
    return m;
  }, [copyQuery.data]);

  const rowFor = (scope: InterestCopyScope, key: string): InterestCopyRow | undefined =>
    byScopeKey.get(`${scope}:${scope === 'general' ? '*' : key.trim().toLowerCase()}`);

  const openEditor = (t: EditTarget) => {
    const existing = rowFor(t.scope, t.key);
    setTarget({ ...t, existing });
    setDraft({
      openers1v1: arrayToLines(existing?.openers1v1),
      openersRoom: arrayToLines(existing?.openersRoom),
      reviewed: Boolean(existing?.reviewed),
    });
    setError(null);
  };

  const closeEditor = () => {
    setTarget(null);
    setDraft(null);
  };

  const onSave = async () => {
    if (!target || !draft) return;
    setError(null);
    try {
      await upsert({
        scope: target.scope,
        key: target.scope === 'general' ? '*' : target.key,
        displayLabel: target.displayLabel,
        openers1v1: linesToArray(draft.openers1v1),
        openersRoom: linesToArray(draft.openersRoom),
        reviewed: draft.reviewed,
      }).unwrap();
      setMessage(`Saved copy for “${target.displayLabel}”`);
      closeEditor();
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message || 'Save failed');
    }
  };

  const onClear = async () => {
    if (!target?.existing) return;
    if (!window.confirm(`Delete all copy for “${target.displayLabel}”?`)) return;
    try {
      await remove(target.existing._id).unwrap();
      setMessage(`Cleared copy for “${target.displayLabel}”`);
      closeEditor();
    } catch {
      setError('Delete failed');
    }
  };

  const patch = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const loading = catalogQuery.isLoading || copyQuery.isLoading;

  // A tappable pill for one catalog entry. Shows opener count + reviewed tick.
  const CopyPill: React.FC<{ scope: InterestCopyScope; keyId: string; label: string }> = ({
    scope,
    keyId,
    label,
  }) => {
    const row = rowFor(scope, keyId);
    const total = (row?.openers1v1.length || 0) + (row?.openersRoom.length || 0);
    return (
      <Chip
        onClick={() => openEditor({ scope, key: keyId, displayLabel: label })}
        icon={row?.reviewed ? <CheckCircleIcon /> : undefined}
        label={
          <Stack direction="row" spacing={0.75} alignItems="center">
            <span>{label}</span>
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: total > 0 ? gridTokens.textPrimary : gridTokens.textSecondary,
                opacity: total > 0 ? 1 : 0.6,
              }}
            >
              {total}
            </Box>
          </Stack>
        }
        variant={total > 0 ? 'filled' : 'outlined'}
        color={row?.reviewed ? 'success' : total > 0 ? 'primary' : 'default'}
        sx={{ mb: 1, mr: 1, cursor: 'pointer' }}
      />
    );
  };

  const categories = catalogQuery.data?.categories || [];

  return (
    <ThemeProvider theme={gridTheme}>
      <Box
        sx={{
          p: { xs: 1.5, sm: 3 },
          minHeight: '100vh',
          bgcolor: gridTokens.bgPrimary,
          color: gridTokens.textPrimary,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <ChatBubbleOutlineIcon color="primary" />
          <Typography variant="h4">Interest Copy</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The opener lines the “In Common” feature writes for <strong>shared interests</strong> (the
          fixed onboarding catalog). Copy composes most-specific&nbsp;→ general: a specific interest’s
          lines, then its category’s lines, then the general fallback. Seed lives in{' '}
          <code>interestCopySeed.ts</code>; edits here write to the DB.
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: gridTokens.bgSecondary }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            What the <code>{'{ }'}</code> placeholders become in the app
          </Typography>
          <PlaceholderLegend />
        </Paper>

        {message ? (
          <Alert severity="success" onClose={() => setMessage(null)} sx={{ mb: 2 }}>
            {message}
          </Alert>
        ) : null}
        {error && !target ? (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* General fallback */}
            <Paper sx={{ p: 2, bgcolor: gridTokens.bgSecondary }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                General fallback
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Applies to <em>any</em> shared interest that has no more-specific copy.
              </Typography>
              <CopyPill scope="general" keyId="*" label="Any interest" />
            </Paper>

            {/* Category-level */}
            <Paper sx={{ p: 2, bgcolor: gridTokens.bgSecondary }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Category copy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Applies to any interest in that category. Great for broad lines (“a night out”, “get
                outside”).
              </Typography>
              <Box>
                {categories.map((c) => (
                  <CopyPill key={c.key} scope="category" keyId={c.key} label={`${c.emoji} ${c.label}`} />
                ))}
              </Box>
            </Paper>

            {/* Interest-level, grouped by category */}
            {categories.map((c) => (
              <Paper key={c.key} sx={{ p: 2, bgcolor: gridTokens.bgSecondary }}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  {c.emoji} {c.label}
                </Typography>
                <Box>
                  {c.options.map((opt) => (
                    <CopyPill key={opt} scope="interest" keyId={opt} label={opt} />
                  ))}
                </Box>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Editor dialog */}
        <Dialog open={!!target} onClose={closeEditor} maxWidth="sm" fullWidth fullScreen={isMobile}>
          {target && draft ? (
            <>
              <DialogTitle>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Stack>
                    <Typography variant="h6">{target.displayLabel}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {target.scope === 'general'
                        ? 'General fallback'
                        : target.scope === 'category'
                          ? 'Category copy'
                          : 'Interest copy'}
                    </Typography>
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        color="success"
                        checked={draft.reviewed}
                        onChange={(e) => patch('reviewed', e.target.checked)}
                      />
                    }
                    label="Human-checked"
                  />
                </Stack>
              </DialogTitle>
              <DialogContent dividers>
                {error ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                ) : null}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  One opener per line. Press Enter to add another.
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1,
                    border: `1px solid ${gridTokens.borderPrimary}`,
                    bgcolor: gridTokens.bgPrimary,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Placeholders you can use (they’re filled in automatically):
                  </Typography>
                  <PlaceholderLegend />
                </Box>
                <Stack spacing={2}>
                  <TextField
                    {...multilineProps}
                    label="1-on-1 openers (profile ice-breakers)"
                    helperText='e.g. "Ask {name} their go-to karaoke song."'
                    value={draft.openers1v1}
                    onChange={(e) => patch('openers1v1', e.target.value)}
                  />
                  <TextField
                    {...multilineProps}
                    label="Room openers (group lines)"
                    helperText='e.g. "{People} here with a go-to karaoke song."'
                    value={draft.openersRoom}
                    onChange={(e) => patch('openersRoom', e.target.value)}
                  />
                </Stack>
                {target.scope === 'interest' ? (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary">
                      Tip: leave these empty to fall back to this interest’s category copy and the
                      general fallback.
                    </Typography>
                  </>
                ) : null}
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                <Button color="error" disabled={!target.existing} onClick={onClear}>
                  Clear
                </Button>
                <Stack direction="row" spacing={1}>
                  <Button onClick={closeEditor}>Cancel</Button>
                  <Button variant="contained" onClick={onSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              </DialogActions>
            </>
          ) : null}
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default InterestCopyPage;
